import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Phone, TrendingDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Icon, SectionHeading } from "../components/Shared";
import TemEstimator from "../components/TemEstimator";
import { company, tem, temPage, caseStudies } from "../mock/mock";

const TEM = () => {
  useEffect(() => {
    document.title = "Telecom Expense Management (TEM) for Business VoIP | Morrisville Communications";
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="brand-gradient text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <span className="inline-flex items-center gap-2 bg-white/15 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-6">
            <TrendingDown className="h-3.5 w-3.5" /> Telecom Expense Management
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold max-w-3xl leading-tight">
            {temPage.heroTitle}
          </h1>
          <p className="mt-5 text-xl text-blue-100 max-w-2xl">{temPage.heroSubtitle}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/contact">
              <Button className="bg-white text-blue-800 hover:bg-blue-50 rounded-full h-12 px-7 font-semibold">
                Get a free bill review <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <a href={`tel:${company.phoneTel}`}>
              <Button variant="outline" className="rounded-full h-12 px-7 font-semibold border-white/40 text-white bg-white/10 hover:bg-white/20">
                <Phone className="h-4 w-4 mr-2" /> {company.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* WHAT IS TEM */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <SectionHeading eyebrow="Overview" title={temPage.whatIs.title} />
            <p className="mt-5 text-lg text-slate-600">{temPage.whatIs.body}</p>
            <div className="mt-6 space-y-3">
              {tem.benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                  <span className="text-slate-700">{b}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-1 gap-5">
            {temPage.whyMatters.map((w) => (
              <Card key={w.title} className="card-hover p-6 rounded-2xl border-slate-100">
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                    <Icon name={w.icon} className="h-5 w-5 text-blue-700" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{w.title}</h3>
                    <p className="text-sm text-slate-600 mt-1">{w.text}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 brand-gradient-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="What we manage" title="A complete TEM program for your VoIP estate" />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tem.features.map((f) => (
              <Card key={f.title} className="card-hover p-7 rounded-2xl border-slate-100 bg-white">
                <div className="h-11 w-11 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                  <Icon name={f.icon} className="h-5 w-5 text-blue-700" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{f.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="How it works" title="Our 5-step TEM process" />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {temPage.process.map((p) => (
              <div key={p.step} className="relative rounded-2xl border border-slate-100 p-6 bg-slate-50">
                <div className="text-4xl font-extrabold text-blue-200">{p.step}</div>
                <h3 className="font-bold text-slate-900 mt-2">{p.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT RESULTS */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-400">Client results</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3">Real savings, delivered</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {caseStudies.items.map((c) => (
              <div key={c.sector} className="rounded-2xl bg-slate-800/60 p-6 border border-slate-700/50 hover:bg-slate-800 transition-colors">
                <div className="text-4xl font-extrabold text-sky-400">{c.savingsPct}</div>
                <div className="text-xs text-slate-400 mt-1">reduction &middot; {c.annual}/yr saved</div>
                <h3 className="font-bold text-white mt-4">{c.sector}</h3>
                <div className="text-xs text-sky-300 mt-0.5">{c.users}</div>
                <p className="text-sm text-slate-300 mt-3">{c.note}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-500 mt-8 max-w-2xl mx-auto">{caseStudies.disclaimer}</p>
        </div>
      </section>

      {/* ESTIMATOR */}
      <section className="py-20 brand-gradient-soft" id="estimator">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading
              eyebrow="Try it now"
              title="Estimate your VoIP savings in seconds"
              subtitle="Move the slider and enter your current monthly bill to see how much a right-sized VoIP phone system could save you. Then book a free review for exact numbers."
            />
            <div className="mt-6 space-y-3">
              {["No cost, no obligation", "Zero-downtime provider switch", "Backed by a 99.999% uptime SLA"].map((t) => (
                <div key={t} className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  <span className="text-slate-700">{t}</span>
                </div>
              ))}
            </div>
          </div>
          <TemEstimator />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="FAQ" title="Telecom Expense Management questions" />
          <Accordion type="single" collapsible className="mt-10">
            {temPage.faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left font-semibold text-slate-900">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default TEM;
